import React, {useCallback} from 'react';
import Box from '@mui/material/Box';
import type {SerializedError} from '@reduxjs/toolkit';
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query/react';
import {useParams} from 'react-router';
import {Concept} from 'types';
import type {HttpError} from 'api/http/types';
import type {Data, Part, Schema} from 'schemaDefinition/types';
import {isFeatureEnabled} from 'configuration';
import {useLocalization} from 'localization';
import {
  useGetAgentQuery,
  useMergeAgentsMutation,
} from 'services/agent/agentApi';
import {useCloseMergeSuggestionAndContinue} from 'services/mergeSuggestions';
import {assertAgentValue} from 'services/utils';
import {mapRtkErrorToHttpError} from 'store/api';
import {Card, FlexBox, Scene, Text, useSnacks} from 'components';
import {ActionButtonV2} from 'components/button/ActionButtonV2';
import {RtkContent} from 'components/rtkContent';
import {DataFormV1} from 'schema';
import {DataFormCompareLayout} from 'schema/form/components/DataFormCompareLayout';
import {useSchema} from 'schemas';
import {AgentWorksTable} from 'widgets/agentWorks';
import {CompareEntities} from 'widgets/compareEntities';
import {useAgentFormConfiguration} from 'editors/agent/hooks/useAgentFormConfiguration';
import {useSceneExtensionsContext} from 'sceneExtensions';

const isNotFoundError = (
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean => error !== undefined && 'status' in error && error.status === 404;

export const MergeAgentsSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const {successSnack, errorSnack} = useSnacks();
  const {currentExtension} = useSceneExtensionsContext();
  const configuration = useAgentFormConfiguration();
  const [mergeAgents] = useMergeAgentsMutation();

  const {targetId: targetIdParam, sourceId: sourceIdParam} = useParams();
  const targetId = targetIdParam || '';
  const sourceId = sourceIdParam || '';

  const {
    data: targetData,
    status: targetStatus,
    error: targetError,
  } = useGetAgentQuery({
    id: targetId,
  });
  const {
    data: sourceData,
    status: sourceStatus,
    error: sourceError,
  } = useGetAgentQuery({
    id: sourceId,
  });

  const targetNotFound = isNotFoundError(targetError);
  const sourceNotFound = isNotFoundError(sourceError);

  const {closeSuggestionAndContinue, currentMergeSuggestion} =
    useCloseMergeSuggestionAndContinue(targetId, sourceId);

  const completeSchema = useSchema({
    entity: Concept.agent,
    subType: targetData?.agentType ?? sourceData?.agentType ?? Concept.person,
  });

  const schema: Schema = {
    ...completeSchema,
    parts: completeSchema.parts.filter(
      p => (p as Part).name !== 'works' && (p as Part).name !== 'prizes',
    ),
  };
  const schemaAgentPrizes: Schema = {
    ...completeSchema,
    parts: completeSchema.parts.filter(p => (p as Part).name === 'prizes'),
  };

  const onSave = useCallback(
    (target: Data, source: Data) => {
      if (!isFeatureEnabled('agentMerge')) {
        errorSnack('Ikke aktivert/implementert ennå');
        return Promise.reject();
      }

      try {
        return mergeAgents({
          target: assertAgentValue(target),
          source: assertAgentValue(source),
        })
          .unwrap()
          .then(() => {
            successSnack('Agentene ble slått sammen');
            return Promise.resolve();
          })
          .catch(error => {
            errorSnack('En feil oppstod!', mapRtkErrorToHttpError(error));
            return Promise.reject();
          });
      } catch (e) {
        errorSnack('En uventet feil oppstod!', e as unknown as HttpError);
        return Promise.reject();
      }
    },
    [errorSnack, mergeAgents, successSnack],
  );

  const targetHeader = (
    <Box
      sx={{
        ml: 2,
      }}>
      <Text kind="primary" variant="h4" textTransform="uppercase">
        Agent som beholdes
      </Text>
      <Text kind="primary" variant="caption">
        Denne agenten vil få alle koblingene, og eventuelle endringer som
        gjøres.
      </Text>
    </Box>
  );
  const sourceHeader = (
    <Box
      sx={{
        ml: 2,
      }}>
      <Text kind="primary" variant="h4" textTransform="uppercase">
        Agent som slettes
      </Text>
      <Text kind="primary" variant="caption">
        Koblingene vil bli flyttet fra denne agenten til den andre agenten, og
        denne agenten vil bli slettet etterpå.
      </Text>
    </Box>
  );

  const renderAgentOrNotFound = (
    id: string,
    data: Data | undefined,
    header: React.ReactNode,
  ) => (
    <Card level="level2">
      {header}
      {data ? (
        <DataFormV1
          id={id}
          schema={schema}
          data={data}
          configuration={configuration}
          mode="read-only"
          relatedData={{}}
        />
      ) : (
        <Box
          sx={{
            mt: 2,
            ml: 2,
          }}>
          <Text kind="primary" variant="body2">
            Fant ikke agenten
          </Text>
        </Box>
      )}
    </Card>
  );

  return (
    <Scene
      browserTitle={t('appbar.mergeAgent')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={t('appbar.mergeAgent')} />
      <Scene.Content>
        <RtkContent
          status={targetStatus}
          error={targetNotFound ? undefined : targetError}>
          <RtkContent
            status={sourceStatus}
            error={sourceNotFound ? undefined : sourceError}>
            {targetData && sourceData ? (
              <CompareEntities
                compareEntitiesContextProps={{
                  targetData,
                  sourceData,
                  schema,
                  onSave,
                }}
                compareEntitiesFormProps={{
                  formConfiguration: configuration,
                  targetHeader,
                  sourceHeader,
                  additionalComparisons: [
                    {
                      target: {
                        schema: schemaAgentPrizes,
                        data: targetData!,
                      },
                      source: {
                        schema: schemaAgentPrizes,
                        data: sourceData!,
                      },
                    },
                  ],
                }}
              />
            ) : targetNotFound || sourceNotFound ? (
              <>
                <FlexBox horizontal right width={'100%'} my={2}>
                  <ActionButtonV2
                    title={'Avslå forslag'}
                    color={'error'}
                    onClick={() => {
                      closeSuggestionAndContinue();
                    }}
                    disabled={!currentMergeSuggestion}
                  />
                </FlexBox>
                <FlexBox horizontal flex={1} mb={4}>
                  <DataFormCompareLayout
                    renderCurrent={() =>
                      renderAgentOrNotFound(
                        'targetDataForm',
                        targetData,
                        targetHeader,
                      )
                    }
                    renderProposed={() =>
                      renderAgentOrNotFound(
                        'sourceDataForm',
                        sourceData,
                        sourceHeader,
                      )
                    }
                  />
                </FlexBox>
              </>
            ) : null}
          </RtkContent>
        </RtkContent>

        <FlexBox horizontal flex={1} mb={4}>
          <DataFormCompareLayout
            renderCurrent={() => (
              <Card level="level2">
                <AgentWorksTable agent={targetData} />
              </Card>
            )}
            renderProposed={() => (
              <Card level="level2">
                <AgentWorksTable agent={sourceData} />
              </Card>
            )}
          />
        </FlexBox>
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
